import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { lastValueFrom } from 'rxjs';

import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';

import { AttachmentModel } from 'src/models/concierge.model';
import { ConciergeApiService } from 'src/shared/services/concierge-api.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';
import { OnboardingComponent } from 'src/shared/components/onboarding/onboarding';

@Component({
    selector: 'create-concierge-action-onboarding',
    templateUrl: './create-concierge-action-onboarding.html',
    standalone: false
})

export class CreateConciergeActionOnboarding implements OnInit {

  uploadButtonTitle: string;
  submitButtonTitle: string;
  itemForm: FormGroup;
  submitted = false;
  attachments: AttachmentModel[] = [];
  errorMessage: string;

  boundSaveItem = this.saveItem.bind(this);

  readonly onboardingProvider = inject(OnboardingProvider);
  private readonly fb = inject(FormBuilder);
  private readonly conciergeApi = inject(ConciergeApiService);
  private readonly util = inject(Util);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  public readonly windowService = inject(WindowService);

  constructor() {
    this.itemForm = this.fb.group({
      body: ''
    });
  }

  ngOnInit() {
    this.uploadButtonTitle = this.getUploadButtonTitle();
    this.submitButtonTitle = this.getSubmitButtonTitle();
  }

  getUploadButtonTitle() {
    const titles = {
      'inventoryAutomation': 'Upload Your Inventory List',
      'addPros': 'Upload Your Pros List',
      'selectFindNewPros': 'Upload Your Pros List',
      'toDos': 'Upload Your Checklist',
      'selectMaintenance': 'Upload Your Maintenance List',
      'selectAccessNotes': 'Upload Your Access Notes',
    }
    return titles[this.onboardingProvider.flow];
  }

  getSubmitButtonTitle() {
    const titles = {
      'inventoryAutomation': 'Save Inventory List',
      'addPros': 'Save Pros',
      'selectFindNewPros': 'Save Preferences',
      'toDos': 'Save Checklist',
      'selectMaintenance': 'Save Maintenance List',
      'selectAccessNotes': 'Save Access Notes',
    }
    return titles[this.onboardingProvider.flow];
  }

  async addAttachment() {
    this.errorMessage = '';
    try {
      const attachment = await this.conciergeApi.addAttachment();
      this.attachments.push(attachment);
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  removeAttachment(index) {
    this.attachments.splice(index, 1);
  }

  async saveItem() {
    const data = {
      ...this.itemForm.value,
      subject: this.getSubject(),
      attachments: this.attachments.map(item => item.fileKey),
      emails: []
    }
    const item = {
      data,
      type: 'support.concierge_task',
    }
    try {
      await Promise.all([
        lastValueFrom(this.conciergeApi.createConciergeItem(item)),
        this.onboardingProvider.markGoalAsCompleted(this.onboardingProvider.flow)
      ]);
      this.rightSidePanelService.setDialogPageTitle('Getting Started');
      this.rightSidePanelService.navigateTo('onboarding', {}, OnboardingComponent);
      this.onboardingProvider.setShowOnboardingInHeader(true);
      const successMessage = 'Action added';
      this.util.showSuccess(successMessage);
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  getSubject() {
    const subjects = {
      'inventoryAutomation': 'Please add my inventory list',
      'addPros': 'Please add my pros list',
      'selectFindNewPros': 'Please save my find new pros preferences',
      'toDos': 'Please add my checklists',
      'selectMaintenance': 'Please add my maintenance list',
      'selectAccessNotes': 'Please add my access notes',
    }
    return subjects[this.onboardingProvider.flow];
  }

}
